import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { 
  Tag, 
  Search, 
  Eye,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Calendar,
  Filter,
  ImageIcon,
  Settings,
  Palette,
  Calculator,
  Layers
} from 'lucide-react';
import { toast } from 'sonner';
import { 
  PriceColorDot, 
  ColorLegend, 
  calculateYearBasedPrice, 
  calculateMonthBasedPrice,
  formatCurrency,
  getColorForModelYear
} from '../components/PriceColorSystem';
import { useAuth } from '../contexts/AuthContext';
import YearColorConfig from '../components/YearColorConfig';
import PriceFormulasConfig from '../components/PriceFormulasConfig';
import BatchFormulaDialog from '../components/BatchFormulaDialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function SecondHand() {
  const { isAdmin } = useAuth();
  const [bicycles, setBicycles] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Pricing configuration
  const [pricingFormula, setPricingFormula] = useState('months');
  const [colorLegend, setColorLegend] = useState([]);
  const [avgPriceByModel, setAvgPriceByModel] = useState({});
  const [isColorConfigOpen, setIsColorConfigOpen] = useState(false);
  const [isFormulasConfigOpen, setIsFormulasConfigOpen] = useState(false);
  const [isBatchFormulaOpen, setIsBatchFormulaOpen] = useState(false);
  
  // Filters
  const [searchModel, setSearchModel] = useState('');
  const [filterModel, setFilterModel] = useState('all');
  const [filterSize, setFilterSize] = useState('all');
  const [filterYear, setFilterYear] = useState('all');
  const [filterColor, setFilterColor] = useState('all');
  const [sortBy, setSortBy] = useState('price_desc'); // price_desc, price_asc, age_desc, age_asc

  useEffect(() => {
    fetchBicycles();
    fetchPricingConfig();
  }, []);

  const fetchBicycles = async () => {
    try {
      const response = await axios.get(`${API}/bicycles`);
      // Filter only active bicycles (not sold)
      const activeBikes = response.data.filter(b => b.status !== 'vendida');
      setBicycles(activeBikes);
    } catch (error) {
      toast.error('Error al cargar las bicicletas');
    } finally {
      setLoading(false);
    }
  };

  const fetchPricingConfig = async () => {
    try {
      const [configRes, legendRes, avgPricesRes] = await Promise.all([
        axios.get(`${API}/config/pricing`),
        axios.get(`${API}/config/color-legend`),
        axios.get(`${API}/bicycles/average-prices`)
      ]);
      setPricingFormula(configRes.data.formula);
      setColorLegend(legendRes.data.legend);
      setAvgPriceByModel(avgPricesRes.data);
    } catch (error) {
      console.error('Error fetching pricing config:', error);
    }
  };

  const handleFormulaChange = async (newFormula) => {
    try {
      await axios.put(`${API}/config/pricing?formula=${newFormula}`);
      setPricingFormula(newFormula);
      toast.success(`Fórmula cambiada a: ${newFormula === 'years' ? 'Por años' : 'Por meses'}`);
    } catch (error) {
      toast.error('Error al cambiar la fórmula');
    }
  };

  // Calculate second-hand price for a bicycle based on selected formula
  const calculateSecondHandPrice = (bike) => {
    if (pricingFormula === 'years') {
      return calculateYearBasedPrice(bike, avgPriceByModel, colorLegend);
    }
    return calculateMonthBasedPrice(bike);
  };

  // Get unique values for filters
  const uniqueModels = useMemo(() => 
    [...new Set(bicycles.map(b => b.model))].sort(), 
    [bicycles]
  );
  
  const uniqueSizes = useMemo(() => 
    [...new Set(bicycles.map(b => b.size))].sort(), 
    [bicycles]
  );
  
  const uniqueYears = useMemo(() => {
    const years = bicycles.map(b => {
      const date = b.purchase_date ? new Date(b.purchase_date) : new Date(b.created_at);
      return date.getFullYear();
    });
    return [...new Set(years)].sort((a, b) => b - a);
  }, [bicycles]);

  const uniqueColors = useMemo(() => {
    // Calculate colors dynamically based on model_year
    const colors = bicycles
      .filter(b => b.model_year)
      .map(b => getColorForModelYear(b.model_year));
    return [...new Set(colors)];
  }, [bicycles]);

  // Filter and sort bicycles
  const filteredBicycles = useMemo(() => {
    let result = bicycles.map(bike => ({
      ...bike,
      priceData: calculateSecondHandPrice(bike)
    }));

    // Apply filters
    if (searchModel) {
      result = result.filter(b => 
        b.model.toLowerCase().includes(searchModel.toLowerCase()) ||
        b.number.toLowerCase().includes(searchModel.toLowerCase())
      );
    }
    
    if (filterModel !== 'all') {
      result = result.filter(b => b.model === filterModel);
    }
    
    if (filterSize !== 'all') {
      result = result.filter(b => b.size === filterSize);
    }
    
    if (filterYear !== 'all') {
      result = result.filter(b => {
        const date = b.purchase_date ? new Date(b.purchase_date) : new Date(b.created_at);
        return date.getFullYear() === parseInt(filterYear);
      });
    }

    if (filterColor !== 'all') {
      result = result.filter(b => b.priceData?.calculatedColor === filterColor);
    }

    // Apply sorting
    switch (sortBy) {
      case 'price_desc':
        result.sort((a, b) => b.priceData.netPrice - a.priceData.netPrice);
        break;
      case 'price_asc':
        result.sort((a, b) => a.priceData.netPrice - b.priceData.netPrice);
        break;
      case 'age_desc':
        result.sort((a, b) => (b.priceData.monthsDiff || b.priceData.yearAge || 0) - (a.priceData.monthsDiff || a.priceData.yearAge || 0));
        break;
      case 'age_asc':
        result.sort((a, b) => (a.priceData.monthsDiff || a.priceData.yearAge || 0) - (b.priceData.monthsDiff || b.priceData.yearAge || 0));
        break;
      default:
        break;
    }

    return result;
  }, [bicycles, searchModel, filterModel, filterSize, filterYear, filterColor, sortBy, pricingFormula, avgPriceByModel, colorLegend]);

  const clearFilters = () => {
    setSearchModel('');
    setFilterModel('all');
    setFilterSize('all');
    setFilterYear('all');
    setFilterColor('all');
    setSortBy('price_desc');
  };

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="secondhand-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-4xl font-bold font-['Barlow_Condensed'] tracking-tight text-slate-900">
            2ª Mano
          </h1>
          <p className="text-slate-500 mt-1">Bicicletas disponibles para venta de segunda mano</p>
        </div>
        <div className="flex items-center gap-3">
          {/* Price Formulas Config Button (admin only) */}
          {isAdmin && (
            <Button variant="outline" size="sm" onClick={() => setIsFormulasConfigOpen(true)} data-testid="formulas-config-btn">
              <Calculator className="w-4 h-4 mr-1" /> Fórmulas
            </Button>
          )}
          {/* Batch Formula Button (admin only) */}
          {isAdmin && (
            <Button variant="outline" size="sm" onClick={() => setIsBatchFormulaOpen(true)} data-testid="batch-formula-btn">
              <Layers className="w-4 h-4 mr-1" /> Lote
            </Button>
          )}
          {/* Color Config Button (admin only, years mode) */}
          {isAdmin && pricingFormula === 'years' && (
            <Button variant="outline" size="sm" onClick={() => setIsColorConfigOpen(true)} data-testid="color-config-btn">
              <Palette className="w-4 h-4 mr-1" /> Colores
            </Button>
          )}
          {/* Formula Selector */}
          <Select value={pricingFormula} onValueChange={handleFormulaChange}>
            <SelectTrigger className="w-44" data-testid="formula-selector">
              <Settings className="w-4 h-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="months">Por meses</SelectItem>
              <SelectItem value="years">Por años (colores)</SelectItem>
            </SelectContent>
          </Select>
          <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200 px-3 py-1">
            <Tag size={14} className="mr-1" />
            {filteredBicycles.length} bicicletas
          </Badge>
        </div>
      </div>

      {/* Filters */}
      <Card className="border-slate-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg font-['Barlow_Condensed'] flex items-center gap-2">
            <Filter size={18} />
            Filtros y Ordenación
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Search and Model filter */}
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
              <Input
                placeholder="Buscar por número o modelo..."
                className="pl-10"
                value={searchModel}
                onChange={(e) => setSearchModel(e.target.value)}
                data-testid="search-input"
              />
            </div>
            <Select value={filterModel} onValueChange={setFilterModel}>
              <SelectTrigger className="w-full sm:w-48" data-testid="model-filter">
                <SelectValue placeholder="Modelo" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los modelos</SelectItem>
                {uniqueModels.map((model) => (
                  <SelectItem key={model} value={model}>{model}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Size, Year, and Sort filters */}
          <div className="flex flex-col sm:flex-row gap-4">
            <Select value={filterSize} onValueChange={setFilterSize}>
              <SelectTrigger className="w-full sm:w-40" data-testid="size-filter">
                <SelectValue placeholder="Talla" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas las tallas</SelectItem>
                {uniqueSizes.map((size) => (
                  <SelectItem key={size} value={size}>{size}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={filterYear} onValueChange={setFilterYear}>
              <SelectTrigger className="w-full sm:w-40" data-testid="year-filter">
                <Calendar size={16} className="mr-2" />
                <SelectValue placeholder="Año" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los años</SelectItem>
                {uniqueYears.map((year) => (
                  <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {pricingFormula === 'years' && (
              <Select value={filterColor} onValueChange={setFilterColor}>
                <SelectTrigger className="w-full sm:w-40" data-testid="color-filter">
                  <SelectValue placeholder="Color" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los colores</SelectItem>
                  {uniqueColors.map((color) => (
                    <SelectItem key={color} value={color}>
                      <div className="flex items-center gap-2">
                        <PriceColorDot color={color} size="sm" />
                        <span className="capitalize">{color}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}

            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-full sm:w-52" data-testid="sort-filter">
                <ArrowUpDown size={16} className="mr-2" />
                <SelectValue placeholder="Ordenar por" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="price_desc">
                  <span className="flex items-center gap-2">
                    <ArrowDown size={14} /> Precio: Mayor a menor
                  </span>
                </SelectItem>
                <SelectItem value="price_asc">
                  <span className="flex items-center gap-2">
                    <ArrowUp size={14} /> Precio: Menor a mayor
                  </span>
                </SelectItem>
                <SelectItem value="age_desc">
                  <span className="flex items-center gap-2">
                    <ArrowDown size={14} /> Antigüedad: Mayor a menor
                  </span>
                </SelectItem>
                <SelectItem value="age_asc">
                  <span className="flex items-center gap-2">
                    <ArrowUp size={14} /> Antigüedad: Menor a mayor
                  </span>
                </SelectItem>
              </SelectContent>
            </Select>

            <Button 
              variant="outline" 
              onClick={clearFilters}
              className="sm:ml-auto"
              data-testid="clear-filters-btn"
            >
              Limpiar filtros
            </Button>
          </div>
          
          {/* Color Legend (only when using years formula) */}
          {pricingFormula === 'years' && colorLegend.length > 0 && (
            <ColorLegend legend={colorLegend} compact />
          )}
        </CardContent>
      </Card>

      {/* Results Table */}
      <Card className="border-slate-200" data-testid="secondhand-table">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="spinner w-8 h-8" />
            </div>
          ) : filteredBicycles.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-slate-400">
              <Tag size={48} className="mb-4" />
              <p>No hay bicicletas que coincidan</p>
              <p className="text-sm">Prueba cambiando los filtros</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    {pricingFormula === 'years' && (
                      <TableHead className="text-xs font-bold uppercase text-slate-500 w-12">Color</TableHead>
                    )}
                    <TableHead className="text-xs font-bold uppercase text-slate-500 w-16">Foto</TableHead>
                    <TableHead className="text-xs font-bold uppercase text-slate-500">Número</TableHead>
                    <TableHead className="text-xs font-bold uppercase text-slate-500">Modelo</TableHead>
                    <TableHead className="text-xs font-bold uppercase text-slate-500">Talla</TableHead>
                    <TableHead className="text-xs font-bold uppercase text-slate-500">Antigüedad</TableHead>
                    <TableHead className="text-xs font-bold uppercase text-slate-500">Precio Neto</TableHead>
                    <TableHead className="text-xs font-bold uppercase text-slate-500">Precio + IVA</TableHead>
                    <TableHead className="text-xs font-bold uppercase text-slate-500 text-right">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredBicycles.map((bike) => (
                    <TableRow key={bike.id} className="table-row-hover" data-testid={`secondhand-row-${bike.id}`}>
                      {pricingFormula === 'years' && (
                        <TableCell className="w-12">
                          <PriceColorDot 
                            color={bike.priceData?.calculatedColor} 
                            size="lg" 
                            showTooltip 
                            yearAge={bike.priceData.yearAge} 
                          />
                        </TableCell>
                      )}
                      <TableCell className="w-16">
                        {bike.photo_url ? (
                          <img 
                            src={`${BACKEND_URL}${bike.photo_url}`}
                            alt={`Bici ${bike.number}`}
                            className="w-12 h-12 object-cover rounded-lg border border-slate-200"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.nextSibling.style.display = 'flex';
                            }}
                          />
                        ) : null}
                        <div 
                          className={`w-12 h-12 bg-slate-100 rounded-lg flex items-center justify-center ${bike.photo_url ? 'hidden' : ''}`}
                        >
                          <ImageIcon size={20} className="text-slate-400" />
                        </div>
                      </TableCell>
                      <TableCell className="font-semibold text-slate-900">{bike.number}</TableCell>
                      <TableCell className="text-slate-600">{bike.model}</TableCell>
                      <TableCell className="text-slate-600">{bike.size}</TableCell>
                      <TableCell>
                        {pricingFormula === 'years' ? (
                          <Badge 
                            variant="outline" 
                            className={bike.priceData.yearAge <= 1 
                              ? 'bg-green-50 text-green-700 border-green-200' 
                              : bike.priceData.yearAge <= 2
                              ? 'bg-amber-50 text-amber-700 border-amber-200'
                              : 'bg-red-50 text-red-700 border-red-200'
                            }
                          >
                            Año {bike.priceData.yearAge} ({bike.priceData.percentage})
                          </Badge>
                        ) : (
                          <Badge 
                            variant="outline" 
                            className={bike.priceData.monthsDiff <= 2 
                              ? 'bg-green-50 text-green-700 border-green-200' 
                              : 'bg-amber-50 text-amber-700 border-amber-200'
                            }
                          >
                            {bike.priceData.monthsDiff} {bike.priceData.monthsDiff === 1 ? 'mes' : 'meses'}
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        {bike.priceData.netPrice > 0 ? (
                          <span className="font-semibold text-green-700">
                            {formatCurrency(bike.priceData.netPrice)}
                          </span>
                        ) : (
                          <span className="text-slate-400">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {bike.priceData.ivaPrice > 0 ? (
                          <span className="font-semibold text-green-800">
                            {formatCurrency(bike.priceData.ivaPrice)}
                          </span>
                        ) : (
                          <span className="text-slate-400">-</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <Link to={`/bicycles/${bike.id}`}>
                          <Button 
                            variant="outline" 
                            size="sm" 
                            className="gap-1"
                            data-testid={`view-bike-${bike.id}`}
                          >
                            <Eye size={14} />
                            Ver
                          </Button>
                        </Link>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Summary */}
      {filteredBicycles.length > 0 && (
        <Card className="border-slate-200 bg-gradient-to-r from-green-50 to-emerald-50">
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="text-sm text-slate-600">
                <strong>{filteredBicycles.length}</strong> bicicletas disponibles para venta de 2ª mano
              </div>
              <div className="flex gap-6 text-sm">
                <div>
                  <span className="text-slate-500">Total Neto: </span>
                  <span className="font-bold text-green-700">
                    {formatCurrency(filteredBicycles.reduce((sum, b) => sum + b.priceData.netPrice, 0))}
                  </span>
                </div>
                <div>
                  <span className="text-slate-500">Total + IVA: </span>
                  <span className="font-bold text-green-800">
                    {formatCurrency(filteredBicycles.reduce((sum, b) => sum + b.priceData.ivaPrice, 0))}
                  </span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Year Color Config Modal */}
      <YearColorConfig open={isColorConfigOpen} onClose={() => { setIsColorConfigOpen(false); fetchPricingConfig(); }} />
      
      {/* Price Formulas Config Modal */}
      <PriceFormulasConfig open={isFormulasConfigOpen} onClose={() => setIsFormulasConfigOpen(false)} />
      
      {/* Batch Formula Dialog */}
      <BatchFormulaDialog open={isBatchFormulaOpen} onClose={() => setIsBatchFormulaOpen(false)} onApplied={fetchBicycles} />
    </div>
  );
}
